import { FlexCell, FlexRow, TabButton } from '@epam/promo';
import { Outlet, useLocation } from 'react-router-dom';

import { ADMIN } from 'common';
import { useNavigationHandler } from 'hooks';
import { UserRole } from 'models/user.model';
import { useUserStore } from 'store/user';
import { useFeatureTogglesStore } from 'store/feature-toggling';
import { taskManagementRoutes } from 'router/taskManagementRoutes';
import { useMemo } from 'react';
import type { FeatureName } from 'models/feature-toggling.model';

const TaskGroupsCaption = 'Task groups';

type IsFeatureEnabled = (feature: FeatureName) => boolean;

interface NavHeader {
  caption: string;
  link: { pathname: string };
  isCurrent: boolean;
  feature?: FeatureName;
}

const NavHeaders: NavHeader[] = [
  {
    caption: 'Tasks',
    link: { pathname: taskManagementRoutes.tasks },
    isCurrent: false,
  },
  {
    caption: 'Recognition',
    link: { pathname: taskManagementRoutes.recognitions },
    isCurrent: false,
    feature: 'recognition',
  },
  {
    caption: TaskGroupsCaption,
    link: { pathname: taskManagementRoutes.taskGroups },
    isCurrent: false,
    feature: 'taskGroups',
  },
];

const getTabs = (
  locationPathname: string,
  role?: UserRole,
  isFeatureEnabled?: IsFeatureEnabled,
) => {
  if (!role) return [];

  let shownTabs =
    role === ADMIN
      ? NavHeaders.filter((h) => ![TaskGroupsCaption].includes(h.caption))
      : NavHeaders;

  if (isFeatureEnabled) {
    shownTabs = shownTabs.filter(
      (tab) => !tab.feature || isFeatureEnabled(tab.feature),
    );
  }

  // Detect the active tab:

  // 1. Sort links by length (longest first)
  const longerLinksFirst = [...shownTabs].sort(
    (a, b) => b.link.pathname.length - a.link.pathname.length,
  );

  // 2. The first matching link will be the longest match
  const activeTab = longerLinksFirst.find((tab) =>
    locationPathname.startsWith(tab.link.pathname),
  );

  // 3. Mark the active tab
  return !activeTab
    ? shownTabs
    : shownTabs.map((tab) => ({
        ...tab,
        isCurrent: tab === activeTab,
      }));
};

export const TaskManagementPage = () => {
  const location = useLocation();
  const user = useUserStore((store) => store.user);
  const { isFeatureEnabled } = useFeatureTogglesStore();
  const handleNavigation = useNavigationHandler();

  const tabs = useMemo(
    () => getTabs(location.pathname, user?.role, isFeatureEnabled),
    [location.pathname, user?.role, isFeatureEnabled],
  );

  return (
    <FlexCell grow={1}>
      <FlexRow borderBottom padding="18">
        {tabs.map(({ caption, link, isCurrent }) => (
          <TabButton
            key={caption}
            caption={caption}
            isActive={isCurrent}
            rawProps={{
              'data-testid': `task-management-tab-${caption.toLowerCase().replaceAll(' ', '-')}`,
            }}
            size="48"
            onClick={() => handleNavigation(link.pathname)}
          />
        ))}
      </FlexRow>
      {/* Main content area (below the fixed header and tabs) */}
      <div style={{ height: 'calc(100vh - 60px - 50px - 2rem)' }}>
        <Outlet />
      </div>
    </FlexCell>
  );
};
